import { useEffect, useState } from "react";
import { Loader2, Landmark, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { DashLayout } from "@/components/DashLayout";
import { PayoutTable } from "@/components/Tables";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, inr } from "@/lib/api";
import { USER_NAV } from "@/pages/navs";

const EMPTY = { holder_name: "", account_number: "", ifsc: "", amount: "" };

export default function UserPayouts() {
  const { user, refresh, tick } = useAuth();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [bens, setBens] = useState([]);
  const [selId, setSelId] = useState(null);
  const sel = bens.find((b) => b.id === selId);

  const loadBens = () => api.get("/me/beneficiaries").then((r) => setBens(r.data));
  useEffect(() => { loadBens(); }, []);

  const pick = (b) => { setSelId(b.id); setF({ holder_name: b.holder_name, account_number: b.account_number, ifsc: b.ifsc, amount: f.amount }); };
  const newAcc = () => { setSelId(null); setF({ ...EMPTY, amount: f.amount }); };
  const removeBen = async (b, e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove ${b.holder_name} (****${b.account_number.slice(-4)})?`)) return;
    try { await api.delete(`/me/beneficiaries/${b.id}`); if (selId === b.id) newAcc(); loadBens(); toast.success("Beneficiary removed"); }
    catch (ex) { toast.error(errMsg(ex)); }
  };

  const load = () => api.get("/me/payouts").then((r) => setRows(r.data));
  useEffect(() => { load(); refresh(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (tick) load(); }, [tick]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/me/payouts", { ...f, amount: Number(f.amount) });
      toast.success("Payout request submitted");
      setF(EMPTY); setSelId(null); load(); refresh(); loadBens();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };

  return (
    <DashLayout nav={USER_NAV} title="Payouts" subtitle="Withdraw from your Payout wallet to bank account">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
        <form onSubmit={submit} className="h-fit min-w-0 space-y-4 rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="payout-form">
          <div className="rounded-lg bg-blue-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-700">Payout wallet balance</div>
            <div className="mt-1 font-display text-2xl font-bold text-[#0A1F44]" data-testid="payout-available-balance">{inr(user?.payout_balance)}</div>
          </div>
          <Beneficiaries bens={bens} selId={selId} onPick={pick} onNew={newAcc} onRemove={removeBen} />
          {sel ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4 text-sm" data-testid="selected-beneficiary">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Paying to</div>
              <div className="mt-1 font-semibold text-[#0A1F44]">{sel.holder_name}</div>
              <div className="font-mono text-xs text-slate-600">A/c {sel.account_number} · {sel.ifsc}</div>
            </div>
          ) : (<>
          <Field label="Account holder name"><Input data-testid="payout-holder-input" required value={f.holder_name} onChange={set("holder_name")} /></Field>
          <Field label="Account number"><Input data-testid="payout-account-input" required inputMode="numeric" value={f.account_number} onChange={(e) => setF({ ...f, account_number: e.target.value.replace(/\D/g, "").slice(0, 20) })} className="font-mono" /></Field>
          <Field label="IFSC code"><Input data-testid="payout-ifsc-input" required maxLength={11} value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value.toUpperCase() })} className="font-mono uppercase" placeholder="SBIN0001234" /></Field>
          </>)}
          <Field label="Amount (₹)"><Input data-testid="payout-amount-input" required type="number" min="1" step="0.01" value={f.amount} onChange={set("amount")} /></Field>
          <button data-testid="payout-submit-btn" disabled={busy} className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#2563EB] font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Request Payout
          </button>
        </form>
        <div className="min-w-0">
          <h2 className="mb-3 font-display text-lg font-semibold text-[#0A1F44]">Payout history</h2>
          <PayoutTable rows={rows} />
        </div>
      </div>
    </DashLayout>
  );
}

export const Field = ({ label, children }) => (
  <label className="block"><span className="text-sm font-medium text-slate-700">{label}</span><div className="mt-1.5">{children}</div></label>
);

const Beneficiaries = ({ bens, selId, onPick, onNew, onRemove }) => (
  <div data-testid="beneficiary-list">
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-medium text-slate-700">Saved beneficiaries</span>
      <button type="button" onClick={onNew} data-testid="new-beneficiary-btn"
        className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ${!selId ? "bg-[#0A1F44] text-white" : "text-[#2563EB] hover:bg-blue-50"}`}>
        <Plus className="h-3.5 w-3.5" /> New account
      </button>
    </div>
    {bens.length === 0 ? (
      <p className="rounded-lg border border-dashed border-slate-200 px-3 py-3 text-xs text-slate-400" data-testid="no-beneficiaries">No saved accounts yet. Accounts you pay are saved automatically.</p>
    ) : (
      <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
        {bens.map((b) => (
          <div key={b.id} role="button" tabIndex={0} onClick={() => onPick(b)} data-testid={`beneficiary-${b.id}`}
            className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${selId === b.id ? "border-[#2563EB] bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
            <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${selId === b.id ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-500"}`}>
              {selId === b.id ? <Check className="h-4 w-4" /> : <Landmark className="h-4 w-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-[#0A1F44]">{b.holder_name}</div>
              <div className="font-mono text-xs text-slate-500">****{b.account_number.slice(-4)} · {b.ifsc}</div>
            </div>
            <button type="button" onClick={(e) => onRemove(b, e)} data-testid={`delete-beneficiary-${b.id}`} className="rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-600">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);
